import React, { useRef } from 'react'
import { CarMileageField } from '@/components/PriceCalculator/CarMileageField'
import { getSortedOffersByPrice } from '@/utils/offers.utils'
import { useFormatter } from '@/utils/useFormatter'
import { datadogRum } from '@datadog/browser-rum'
import { Badge } from 'ui/src/components/Badge/Badge'
import { Select } from 'ui/src/components/Select/Select'
import { xStack } from 'ui/src/patterns/stack'
import { yStack } from 'ui/src/patterns/stack'
import { MILEAGE_DATA_KEY, SELECTED_TIER_KEY, useCarExtensionOffers } from './CarTrialExtensionOffers'

type Props = {
  isUpsell?: boolean
}

export const EditExtensionOfferForm = ({ isUpsell }: Props) => {
  const formRef = useRef<HTMLFormElement>(null)
  const formatter = useFormatter()

  const { offers, selectedOffer, selectedMilage, onTierChange, editExtensionOffers } = useCarExtensionOffers()

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const formData = new FormData(event.currentTarget)

    const selectedTier = formData.get(SELECTED_TIER_KEY) as string
    const mileage = formData.get(MILEAGE_DATA_KEY) as string

    const data = {
      [MILEAGE_DATA_KEY]: mileage,
    }

    if (typeof selectedTier !== 'string') {
      throw new Error('Missing offer')
    }

    const hasChangedTierLevel = selectedTier !== selectedOffer.variant.typeOfContract

    editExtensionOffers(data)

    datadogRum.addAction('Offer Car Save', {
      changedMileage: data[MILEAGE_DATA_KEY] !== data[MILEAGE_DATA_KEY],
      changedTierLevel: hasChangedTierLevel,
    })
  }

  const handleFieldChange = (data: { field: typeof SELECTED_TIER_KEY | typeof MILEAGE_DATA_KEY; value: string }) => {
    if (data.field === SELECTED_TIER_KEY) {
      // We only want to set the selected tier, but no need to submit the form since we already have data for all tiers at this point
      onTierChange(data.value)
    } else {
      formRef.current?.requestSubmit()
    }
  }

  const sortedOffers = getSortedOffersByPrice(offers)

  const options = sortedOffers.map((offer) => ({
    name: offer.variant.displayName,
    value: offer.variant.typeOfContract,
    price: formatter.monthlyPrice({ amount: offer.cost.gross.amount, currencyCode: offer.cost.gross.currencyCode }),
  }))

  const mileageField = {
    type: 'car-mileage',
    name: MILEAGE_DATA_KEY,
    label: { key: 'FIELD_MILEAGE_LABEL' },
    required: true,
  } as const

  return (
    <form ref={formRef} className={yStack({ gap: 'xxs' })} onSubmit={handleSubmit}>
      <Select.Root
        name={SELECTED_TIER_KEY}
        onValueChange={(value) => {
          handleFieldChange({ field: SELECTED_TIER_KEY, value })
        }}
        defaultValue={selectedOffer.variant.typeOfContract}
      >
        <Select.Trigger>
          <Select.Value />
        </Select.Trigger>

        <Select.Content data-theme={isUpsell && 'green'}>
          <Select.Options>
            {options.map((option) => (
              <Select.Item key={option.value} value={option.value} asChild>
                <div className={xStack({ justifyContent: 'space-between' })}>
                  <Select.ItemText>{option.name}</Select.ItemText>
                  <Badge variant="grey1">{option.price}</Badge>
                </div>
              </Select.Item>
            ))}
          </Select.Options>
        </Select.Content>
      </Select.Root>

      <CarMileageField
        field={mileageField}
        defaultValue={selectedMilage}
        onValueChange={(value) => {
          handleFieldChange({ field: MILEAGE_DATA_KEY, value })
        }}
      />
    </form>
  )
}
